import moment from "moment";
import { FormDataOptions } from "src/app/models/models";
import { FeedbackTemplateListItem, FeedbackTemplateRequest } from "./feedback.types";

const DATETIME_LOCAL_INPUT_FORMATS = ["YYYY-MM-DDTHH:mm", "YYYY-MM-DDTHH:mm:ss"];
const DATETIME_LOCAL_INPUT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/;

export interface AttachableFeedbackForm {
	id: string;
	name: string;
	formType?: FormDataOptions;
	feedbackTemplateId?: string | null;
}

export function buildMoveFormsConfirmation(formNames: string[]): string {
	const names = formNames.map((name) => name.trim()).filter((name) => name.length > 0);
	const quoted = names.map((name) => `"${name}"`);
	if (quoted.length <= 1) {
		const name = quoted[0] ?? '"This form"';
		return `${name} already has a feedback form attached. Are you sure you want to replace it with this new feedback form?`;
	}

	const listed =
		quoted.length === 2
			? `${quoted[0]} and ${quoted[1]}`
			: `${quoted.slice(0, -1).join(", ")}, and ${quoted[quoted.length - 1]}`;

	return `${listed} already have a feedback form attached. Are you sure you want to replace them with this new feedback form?`;
}

export interface FeedbackQuestionDraft {
	id?: string;
	prompt?: string;
	required?: boolean;
	answerEnabled?: boolean;
	answerRequired?: boolean;
}

export const UNSUPPORTED_STANDALONE_RECIPIENT_ROLES = ["admin", "office admin"] as const;

export const DEPRECATED_STANDALONE_RECIPIENT_ROLE_LABELS: Record<string, string> = {
	admin: "Admin",
	"office admin": "Office Admin",
};

export function sanitizeStandaloneRecipientRoles(roles: string[] | null | undefined): {
	roles: string[];
	removedRoles: string[];
} {
	const removedRoles: string[] = [];

	const sanitizedRoles = (roles ?? []).filter((role) => {
		if (typeof role !== "string" || !role.trim()) {
			return false;
		}

		const normalized = role.trim();
		if ((UNSUPPORTED_STANDALONE_RECIPIENT_ROLES as readonly string[]).includes(normalized)) {
			if (!removedRoles.includes(normalized)) {
				removedRoles.push(normalized);
			}
			return false;
		}

		return true;
	});

	return { roles: sanitizedRoles, removedRoles };
}

export interface FeedbackTemplateLinkState {
	isStandalone: boolean;
	formTypeCodes: string[];
	formIds: string[];
}

export function resolveFeedbackTemplateLinks(data: FeedbackTemplateListItem): FeedbackTemplateLinkState {
	const linkedForms = data.linkedForms ?? [];
	const formTypeCodes = data.formTypeCodes?.length
		? data.formTypeCodes
		: data.formTypeCode
			? [data.formTypeCode]
			: linkedForms[0]?.formTypeCode
				? [linkedForms[0].formTypeCode]
				: [];
	const formIds = data.formIds?.length
		? data.formIds
		: linkedForms.length
			? linkedForms.map((form) => form.id)
			: data.formId
				? [data.formId]
				: [];

	return {
		isStandalone: data.isStandalone === true,
		formTypeCodes,
		formIds,
	};
}

export function mapLinkedFeedbackForms(
	linkedForms: FeedbackTemplateListItem["linkedForms"] = [],
): AttachableFeedbackForm[] {
	return linkedForms.map((form) => ({
		id: form.id,
		name: form.name,
		formType: {
			code: form.formTypeCode,
			description: form.formTypeLabel,
		},
	}));
}

export function formatAttachedFormNames(row: FeedbackTemplateListItem): string {
	if (row.isStandalone) {
		return "N/A";
	}
	if (Array.isArray(row.linkedForms)) {
		return row.linkedForms.map((form) => form.name).join(", ") || "-";
	}
	return row.formName || "-";
}

/** UTC instant from API → local `Date` for datetime pickers. */
export function utcIsoToLocalDate(value?: string | null): Date | null {
	if (!value?.trim()) return null;
	const parsed = moment.utc(value);
	return parsed.isValid() ? parsed.local().toDate() : null;
}

/** Local `Date` from picker → UTC ISO for API. */
export function localDateToUtcIso(value?: Date | string | null): string | undefined {
	if (!value) return undefined;
	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
	}
	return localDatetimeInputToUtcIso(value);
}

/** UTC instant from API → local wall-clock value for `<input type="datetime-local">`. */
export function utcIsoToLocalDatetimeInput(value?: string | null): string {
	if (!value?.trim()) return "";
	const parsed = moment.utc(value);
	if (!parsed.isValid()) return "";
	return parsed.local().format("YYYY-MM-DDTHH:mm");
}

/** Local wall-clock value from `<input type="datetime-local">` → UTC ISO for API. */
export function localDatetimeInputToUtcIso(value?: string | null): string | undefined {
	const trimmed = value?.trim();
	if (!trimmed || !DATETIME_LOCAL_INPUT_PATTERN.test(trimmed)) return undefined;
	const parsed = moment(trimmed, DATETIME_LOCAL_INPUT_FORMATS, true);
	if (!parsed.isValid()) return undefined;
	return parsed.utc().toISOString();
}

/** UTC instant from API → formatted local display string. */
export function formatUtcIsoForLocalDisplay(value?: string | null, format = "DD MMM YYYY h:mm A"): string {
	if (!value?.trim()) return "";
	const parsed = moment.utc(value);
	if (!parsed.isValid()) return "";
	return parsed.local().format(format);
}

export function buildFeedbackTemplateRequest(params: {
	isEdit: boolean;
	standalone: boolean;
	name: string;
	recipientRoles?: string[];
	formIds?: string[];
	formTypeCodes?: string[];
	replaceExistingAttachments?: boolean;
	startsAt?: string | Date | null;
	endsAt?: string | Date | null;
	questions: FeedbackQuestionDraft[];
}): FeedbackTemplateRequest {
	const payload: FeedbackTemplateRequest = {
		name: params.name.trim(),
		isActive: true,
		questions: params.questions.map((question, index) => {
			const questionId = String(question.id ?? "").trim();
			const required = question.required !== false;
			const answerEnabled = question.answerEnabled !== false;
			return {
				...(questionId ? { id: questionId } : {}),
				sortOrder: index,
				prompt: String(question.prompt ?? "").trim(),
				required,
				answerEnabled,
				answerRequired: answerEnabled && question.answerRequired === true,
			};
		}),
	};

	if (!params.isEdit) {
		payload.isStandalone = params.standalone;
	}

	if (params.standalone) {
		payload.recipientRoles = sanitizeStandaloneRecipientRoles(params.recipientRoles).roles;
		payload.startsAt = localDateToUtcIso(params.startsAt) ?? null;
		payload.endsAt = localDateToUtcIso(params.endsAt) ?? null;
	} else {
		payload.formIds = (params.formIds ?? []).filter((id) => typeof id === "string" && id.trim().length > 0);
		payload.formTypeCodes = params.formTypeCodes ?? [];
		if (params.replaceExistingAttachments) {
			payload.replaceExistingAttachments = true;
		}
	}

	return payload;
}
